import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useConfirm } from "@/components/ui/confirm-dialog";
import {
  useDeleteDailyDishMutation,
  useGetDailyDishQuery,
} from "@/redux/services/dailyDishes/dailyDishes.api";
import { DailyDish } from "@/redux/services/dailyDishes/dailyDishes.type";
import {
  cn,
  convertPriceFromCent,
  formatDate,
  formatPrice,
  formatTimeDisplay,
  getDayLabel,
  getImageUrl,
} from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import { usePermissions } from "@/hooks/usePermissions";
import { useAppSelector } from "@/redux/features/hooks";
import DailyDishIngredients from "@/components/inventory/recipes/DailyDishIngredients";
import { ChevronDown, Pencil, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

const Section = ({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <section>
        <CollapsibleTrigger className="w-full flex items-center justify-between py-2 border-b border-border text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-card-foreground transition-colors">
          <span>{title}</span>
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform",
              open && "rotate-180"
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4 space-y-3">
          {children}
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
};

const MetaRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-card-foreground font-medium text-right">{value}</span>
  </div>
);

interface Props {
  dailyDishId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (dish: DailyDish) => void;
}

const DailyDishDetailsDrawer = ({
  dailyDishId,
  isOpen,
  onClose,
  onEdit,
}: Props) => {
  const currency = useRestaurantCurrency();
  const confirm = useConfirm();
  const restaurant = useAppSelector((state) => state.user.currentRestaurant);
  // Recipes follow the API's inventory permissions (SFRESTO-136): the
  // section shows on inventory.view; editing needs inventory.manage, which
  // the recipe editor checks itself.
  const { can } = usePermissions();
  const canViewRecipe = can("inventory.view");

  const { data: dish, isFetching } = useGetDailyDishQuery(dailyDishId ?? "", {
    skip: !dailyDishId,
  });

  const [deleteDailyDish, { isLoading: isDeleting }] =
    useDeleteDailyDishMutation();

  const handleDelete = async () => {
    if (!dish?.id) return;
    const ok = await confirm({
      title: `Supprimer "${dish.name}" ?`,
      description: "Cette action est irréversible.",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    try {
      await deleteDailyDish(dish.id).unwrap();
      toast.success("Plat du jour supprimé");
      onClose();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const scheduleText =
    dish && (dish.startTime || dish.endTime)
      ? `${formatTimeDisplay(dish.startTime)} - ${formatTimeDisplay(
          dish.endTime
        )}`
      : "Toute la journée";

  return (
    <Sheet open={isOpen} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col gap-0"
      >
        <header className="flex-shrink-0 px-6 py-4 border-b border-border bg-card">
          <SheetTitle className="text-base">
            {dish ? dish.name : "Détails du plat du jour"}
          </SheetTitle>
          <SheetDescription hidden>
            Détails et actions pour ce plat du jour.
          </SheetDescription>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
          {isFetching && !dish ? (
            <div className="space-y-3">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : dish ? (
            <>
              <Section title="Aperçu">
                {dish.image?.url && (
                  <div className="w-32 h-32 bg-muted/50 rounded-full overflow-hidden border-2 border-border">
                    <img
                      src={getImageUrl(dish.image.url)}
                      alt={dish.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <MetaRow
                    label="Prix"
                    value={
                      <span className="text-primary font-semibold">
                        {formatPrice(convertPriceFromCent(dish.price), currency)}
                      </span>
                    }
                  />
                  <MetaRow label="Position" value={dish.sortOrder} />
                  {dish.product && typeof dish.product === "object" && (
                    <MetaRow label="Plat" value={dish.product.title} />
                  )}
                </div>
                {dish.description && (
                  <p className="text-sm text-muted-foreground bg-muted/50 rounded-md p-3">
                    {dish.description}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    variant={dish.isActive ? "default" : "secondary"}
                    className={cn(dish.isActive ? "bg-primary" : "")}
                  >
                    {dish.isActive ? "Actif" : "Inactif"}
                  </Badge>
                  {dish.isCurrentlyActive && (
                    <Badge variant="default" className="bg-green-500">
                      Disponible maintenant
                    </Badge>
                  )}
                </div>
                {dish.tags && dish.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {dish.tags.map((tag) => (
                      <Badge key={tag} variant="outline">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </Section>

              <Section title="Disponibilité">
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">
                    Jours
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {[...(dish.daysOfWeek ?? [])]
                      .sort((a, b) => a - b)
                      .map((day) => (
                        <Badge key={day} variant="secondary">
                          {getDayLabel(day)}
                        </Badge>
                      ))}
                  </div>
                </div>
                <MetaRow label="Horaires" value={scheduleText} />
              </Section>

              {canViewRecipe && restaurant && dish.id && (
                <Section title="Ingrédients" defaultOpen={false}>
                  <DailyDishIngredients
                    dailyDishId={dish.id}
                    restaurantId={restaurant.id}
                  />
                </Section>
              )}

              <Section title="Métadonnées" defaultOpen={false}>
                <MetaRow
                  label="Créé le"
                  value={formatDate(new Date(dish.createdAt))}
                />
                {dish.updatedAt && (
                  <MetaRow
                    label="Modifié le"
                    value={formatDate(new Date(dish.updatedAt))}
                  />
                )}
              </Section>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Impossible de charger les détails.
            </p>
          )}
        </div>

        <footer className="flex-shrink-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-2">
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={!dish || isDeleting}
          >
            <Trash2 className="h-4 w-4" />
            Supprimer
          </Button>
          <Button
            type="button"
            variant="accent"
            onClick={() => dish && onEdit(dish)}
            disabled={!dish}
          >
            <Pencil className="h-4 w-4" />
            Modifier
          </Button>
        </footer>
      </SheetContent>
    </Sheet>
  );
};

export default DailyDishDetailsDrawer;
